import React, { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { MessageCircle, X, Phone } from "lucide-react";

// WhatsApp brand green
const WA_GREEN = "#25D366";
const WA_NUMBER = "919963645702"; // +91 9963645702, no + or spaces for wa.me
const WA_DISPLAY = "+91 99636 45702";

const DEFAULT_MSG =
  "Hi DB Computronics team — I would like to discuss an enterprise IT requirement.";

const waLink = (msg) =>
  `https://wa.me/${WA_NUMBER}?text=${encodeURIComponent(msg || DEFAULT_MSG)}`;

const PRESET = [
  "Hi, I'd like a quote for computer peripherals (HP / Dell / Lenovo).",
  "I need a networking rollout across multiple sites — please share options.",
  "We're planning a CCTV surveillance deployment. Can we discuss?",
  "Looking for UPS and power backup solutions for our office.",
  "Sharing a government tender — please help with documentation & pricing.",
];

export default function WhatsAppFab() {
  const [open, setOpen] = useState(false);
  const [text, setText] = useState(DEFAULT_MSG);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const t = setTimeout(() => setVisible(true), 900);
    return () => clearTimeout(t);
  }, []);

  useEffect(() => {
    const onKey = (e) => { if (e.key === "Escape") setOpen(false); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  if (!visible) return null;

  return (
    <>
      {/* Chat panel */}
      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, y: 20, scale: 0.96 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 20, scale: 0.96 }}
            transition={{ duration: 0.35, ease: [0.16, 1, 0.3, 1] }}
            className="fixed bottom-24 right-6 md:right-8 z-[55] w-[min(360px,calc(100vw-2rem))] rounded-2xl overflow-hidden shadow-[0_30px_80px_-20px_rgba(0,0,0,0.55)] border border-black/10 bg-[#FAFAFA] text-[#222222]"
            data-testid="whatsapp-panel"
            role="dialog"
            aria-label="Chat with DB Computronics on WhatsApp"
          >
            {/* Header */}
            <div className="px-5 py-4 flex items-center gap-3" style={{ background: WA_GREEN }}>
              <div className="w-10 h-10 rounded-full bg-white/95 flex items-center justify-center">
                <img src="/brand/logo.png" alt="DB Computronics" className="w-8 h-8 rounded-md object-contain" />
              </div>
              <div className="flex-1 text-white">
                <div className="font-btn text-[14px] leading-tight">DB Computronics</div>
                <div className="text-[11px] opacity-90 flex items-center gap-1.5 mt-0.5">
                  <span className="inline-block w-1.5 h-1.5 rounded-full bg-white animate-pulse" />
                  Typically replies within an hour
                </div>
              </div>
              <button
                onClick={() => setOpen(false)}
                className="text-white/90 hover:text-white p-1"
                aria-label="Close WhatsApp panel"
                data-testid="whatsapp-close"
              >
                <X size={18} />
              </button>
            </div>

            {/* Body */}
            <div
              className="px-5 py-5"
              style={{
                backgroundImage:
                  "radial-gradient(rgba(0,0,0,0.04) 1px, transparent 1px)",
                backgroundSize: "12px 12px",
                backgroundColor: "#ECE5DD",
              }}
            >
              <div className="bg-white rounded-lg rounded-tl-sm px-4 py-3 shadow-sm max-w-[85%]">
                <div className="text-[10px] tracking-[0.22em] uppercase text-[#9A9A9A] font-btn mb-1">DB Computronics</div>
                <div className="text-[13px] leading-relaxed">
                  Hi there 👋 — how can we help you today? Share a quick line and we'll pick it up on WhatsApp.
                </div>
              </div>

              <div className="mt-4 flex flex-wrap gap-2">
                {PRESET.map((p, i) => (
                  <button
                    key={i}
                    onClick={() => setText(p)}
                    className="text-left text-[11px] leading-snug bg-white border border-black/8 rounded-full px-3 py-1.5 hover:border-[#A4161A] hover:text-[#A4161A] transition-colors duration-300"
                    data-testid={`whatsapp-preset-${i}`}
                  >
                    {p.length > 42 ? p.slice(0, 40) + "…" : p}
                  </button>
                ))}
              </div>

              <textarea
                value={text}
                onChange={(e) => setText(e.target.value)}
                rows={3}
                className="mt-4 w-full resize-none rounded-lg border border-black/10 bg-white p-3 text-[13px] outline-none focus:border-[#25D366] transition-colors"
                placeholder="Type your message…"
                data-testid="whatsapp-textarea"
              />

              <a
                href={waLink(text)}
                target="_blank"
                rel="noopener noreferrer"
                onClick={() => setTimeout(() => setOpen(false), 400)}
                className="mt-3 inline-flex w-full items-center justify-center gap-2 rounded-lg px-4 py-3 font-btn text-[12px] tracking-[0.22em] uppercase text-white transition-transform duration-300 hover:brightness-110 active:scale-[0.98]"
                style={{ background: WA_GREEN }}
                data-testid="whatsapp-send"
              >
                <MessageCircle size={16} />
                <span>Start Chat on WhatsApp</span>
              </a>

              <a
                href={`tel:+${WA_NUMBER}`}
                className="mt-2 flex items-center justify-center gap-2 text-[11px] tracking-[0.2em] uppercase font-btn text-[#444444] hover:text-[#A4161A] transition-colors py-2"
                data-testid="whatsapp-call"
              >
                <Phone size={12} />
                <span>Or call {WA_DISPLAY}</span>
              </a>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Floating action button */}
      <motion.button
        initial={{ opacity: 0, scale: 0.6 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ duration: 0.55, ease: [0.16, 1, 0.3, 1] }}
        onClick={() => setOpen((v) => !v)}
        className="fixed bottom-6 right-6 md:bottom-8 md:right-8 z-[55] group flex items-center gap-3"
        aria-label={open ? "Close WhatsApp chat" : "Chat on WhatsApp"}
        data-testid="whatsapp-fab"
      >
        {!open && (
          <span className="hidden md:inline-flex items-center gap-2 pl-4 pr-5 py-3 rounded-full bg-[#222222]/90 backdrop-blur-md text-[#FAFAFA] text-[11px] tracking-[0.22em] uppercase font-btn border border-white/10 shadow-lg opacity-0 group-hover:opacity-100 -translate-x-1 group-hover:translate-x-0 transition-all duration-500 pointer-events-none">
            Chat with sales
          </span>
        )}

        <span
          className="relative flex items-center justify-center w-14 h-14 md:w-16 md:h-16 rounded-full text-white shadow-[0_18px_40px_-10px_rgba(37,211,102,0.55)] hover:brightness-110 active:scale-95 transition-all duration-300"
          style={{ background: WA_GREEN }}
        >
          {!open && (
            <>
              <span className="absolute inset-0 rounded-full animate-ping opacity-40" style={{ background: WA_GREEN }} />
              <span className="absolute -top-1 -right-1 w-4 h-4 rounded-full bg-[#A4161A] border-2 border-[#222222] text-[9px] font-btn flex items-center justify-center">
                1
              </span>
            </>
          )}
          {open ? (
            <X size={22} />
          ) : (
            // WhatsApp glyph
            <svg viewBox="0 0 32 32" width="26" height="26" aria-hidden="true" fill="currentColor">
              <path d="M19.11 17.205c-.372 0-1.088 1.39-1.518 1.39a.63.63 0 0 1-.315-.1c-.802-.402-1.504-.817-2.163-1.447-.545-.516-1.146-1.29-1.46-1.963a.426.426 0 0 1-.073-.215c0-.33.99-.945.99-1.49 0-.143-.73-2.09-.832-2.335-.143-.372-.214-.487-.6-.487-.187 0-.36-.043-.53-.043-.302 0-.53.115-.746.315-.688.645-1.032 1.318-1.06 2.264v.114c-.015.99.472 1.977 1.017 2.78 1.23 1.82 2.506 3.41 4.554 4.34.616.287 2.035.888 2.708.888.472 0 2.335-.487 2.335-1.318 0-.128-.014-.243-.043-.372-.114-.688-1.978-1.376-2.264-1.32ZM16.056 6.365c-5.478 0-9.933 4.455-9.933 9.933 0 1.888.53 3.734 1.53 5.334L6.075 26.35l4.87-1.577a9.925 9.925 0 0 0 5.11 1.415c5.477 0 9.932-4.455 9.932-9.933 0-5.478-4.455-9.89-9.932-9.89Zm0 18.146a8.202 8.202 0 0 1-4.4-1.288l-.315-.187-3.238 1.048 1.045-3.152-.2-.33a8.185 8.185 0 0 1-1.32-4.44c0-4.554 3.696-8.267 8.243-8.267 4.598 0 8.267 3.669 8.267 8.267.028 4.583-3.684 8.35-8.082 8.35Z" />
            </svg>
          )}
        </span>
      </motion.button>
    </>
  );
}
